import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import { escaparHtml, htmlParaEditor } from '../../lib/htmlSeguro';
import { describirErrorRed, mensajeErrorRed } from '../../lib/erroresRed';
import { destinatariosDeRespuesta } from '../../lib/destinatarios';
import { api } from '../../lib/api';
import { irA } from '../../lib/dominios';
import { precalentarMotor, tiposDeAdjuntos } from '../../lib/precargaMotor';
import { editorUrlFor } from '../../pages/DriveEditor';
import { OFFICE_FILE_RE, officeKind } from '../office/OfficeEditor';
import { OFFICE_APPS, OFFICE_NAME } from '../office/icons';
import SenderAvatar from './SenderAvatar';
import { LineaVisto } from './Visto';
import TarjetaInvitacion from './TarjetaInvitacion';
import ConfirmarBorradoDefinitivo, { avisoEliminados } from './BorradoDefinitivo';
import { useStore } from '../../store/useStore';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';
import { toast } from 'sonner';
import { fmtFull, fmtDate, initials, humanSize } from '../../lib/format';
import { avisoArchivado, siguienteTrasMover } from '../../lib/trasMover';
import { textoProgramado } from '../../lib/programar';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import {
  Reply, ReplyAll, Forward, Archive, Trash2, MoreVertical, Printer, Download, Code,
  Paperclip, Image, MailOpen, Loader2, ArrowLeft, Maximize2, Minimize2, ChevronDown,
  ChevronUp, AlertOctagon, Clock, Filter, Palette, Star, FolderInput, Inbox, Folder,
  ExternalLink, HardDriveUpload, Check, AlertTriangle, Mail,
} from 'lucide-react';

/* Las mismas pestañas que enseña la lista, con el nombre que ve la persona.
   Deben coincidir con CATEGORIAS_MANUALES del backend. */
const CATEGORIAS_NOMBRE = {
  primary: 'Principales',
  updates: 'Novedades',
  promotions: 'Promociones',
  social: 'Social',
};

/* Tonos de avatar del diseño: fondo tenue + texto del mismo tono. */
const AV_TONES = [
  ['var(--accent-soft)', 'var(--accent)'],
  ['var(--l-client-bg)', 'var(--l-client)'],
  ['var(--l-inv-bg)', 'var(--l-inv)'],
  ['var(--l-proj-bg)', 'var(--l-proj)'],
  ['var(--hover)', 'var(--text-2)'],
];
function avatarTone(seed) {
  const s = seed || '';
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % AV_TONES.length;
  return AV_TONES[Math.abs(h)];
}

/* El cuerpo del correo vive en un iframe aislado: los tokens del tema no llegan
   por herencia, así que se leen y se inyectan como valores. */
function themeTokens() {
  try {
    const cs = getComputedStyle(document.documentElement);
    const g = (name, fallback) => (cs.getPropertyValue(name) || '').trim() || fallback;
    const dark = document.documentElement.classList.contains('dark');
    return {
      dark,
      scheme: dark ? 'dark' : 'light',
      text: g('--text', '#1a1b22'),
      text2: g('--text-2', '#575b6b'),
      accent: g('--accent', '#2563eb'),
      line: g('--line-2', '#d6d9e6'),
      panel2: g('--panel-2', '#ffffff'),
    };
  } catch {
    return { dark: false, scheme: 'light', text: '#1a1b22', text2: '#575b6b', accent: '#2563eb', line: '#d6d9e6', panel2: '#ffffff' };
  }
}

/* ---- Adaptación de los colores del correo al tema -------------------------
   Los correos "planos" (la mayor parte del correo interno) no traen fondo y ya
   heredan el del lector. El problema son los que fijan blanco explícito por
   `bgcolor` o `style` en línea: en oscuro deslumbran. Aquí se neutralizan SOLO
   los fondos casi blancos y los textos casi negros (y al revés en claro),
   dejando intactos los colores de marca, los degradados y las imágenes.
   Se trabaja sobre el HTML YA SANEADO y solo se QUITAN declaraciones de color:
   nunca se añade marcado, así que no afecta a la seguridad. */
function parseColor(value) {
  if (!value) return null;
  const s = String(value).trim().toLowerCase();
  if (!s || s === 'transparent' || s === 'inherit' || s === 'initial' || s === 'currentcolor' || s === 'unset') return null;
  let m = /^#([0-9a-f]{3,8})$/.exec(s);
  if (m) {
    let h = m[1];
    if (h.length === 3 || h.length === 4) h = h.split('').map((c) => c + c).join('');
    if (h.length < 6) return null;
    return {
      r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16),
      a: h.length >= 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1,
    };
  }
  m = /^rgba?\(([^)]+)\)$/.exec(s);
  if (m) {
    const p = m[1].split(/[\s,/]+/).filter(Boolean).map(parseFloat);
    if (p.length < 3 || p.slice(0, 3).some(isNaN)) return null;
    return { r: p[0], g: p[1], b: p[2], a: p.length > 3 && !isNaN(p[3]) ? p[3] : 1 };
  }
  if (s === 'white') return { r: 255, g: 255, b: 255, a: 1 };
  if (s === 'black') return { r: 0, g: 0, b: 0, a: 1 };
  return null; // otros nombres con color propio: se respetan
}

function luminance(c) {
  const f = (x) => { const v = Math.min(255, Math.max(0, x)) / 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
  return 0.2126 * f(c.r) + 0.7152 * f(c.g) + 0.0722 * f(c.b);
}

function adaptEmailColors(html, dark) {
  try {
    if (typeof DOMParser === 'undefined') return html;
    const doc = new DOMParser().parseFromString(`<!doctype html><html><body>${html}</body></html>`, 'text/html');
    // En oscuro: fuera fondos casi blancos y textos casi negros. En claro NO se
    // quita ningún fondo: un fondo oscuro en un correo es diseño (el botón negro
    // de Anthropic, una cabecera de marca) y quitarlo dejaba el botón invisible
    // con su texto blanco encima. En claro solo se rescata texto blanco suelto.
    const dropBg = (c) => (dark ? luminance(c) > 0.82 : false);
    const dropFg = (c) => (dark ? luminance(c) < 0.22 : luminance(c) > 0.88);
    // Elementos que CONSERVAN un fondo propio: sus descendientes no se tocan.
    const conFondo = new WeakSet();
    // querySelectorAll devuelve en orden de documento (padres antes que hijos),
    // así que al llegar a un hijo ya sabemos si algún ancestro guardó su fondo.
    doc.body.querySelectorAll('*').forEach((el) => {
      let heredaFondo = false;
      for (let p = el.parentElement; p && p !== doc.body; p = p.parentElement) {
        if (conFondo.has(p)) { heredaFondo = true; break; }
      }

      // Un fondo que contiene imágenes NO se toca: solo sabemos juzgar colores,
      // no lo que hay dentro de un PNG. Quitar el azul de una cabecera deja
      // invisible el logotipo blanco que iba encima (le pasaba a IONOS).
      const llevaImagen = !!el.querySelector('img');

      let fondoPropio = false;
      const bgAttr = el.getAttribute('bgcolor');
      if (bgAttr) {
        const c = parseColor(bgAttr);
        if (c && c.a > 0.05) {
          if (dropBg(c) && !llevaImagen) el.removeAttribute('bgcolor');
          else fondoPropio = true;
        }
      }
      const st = el.style;
      if (st && st.length) {
        const bc = parseColor(st.backgroundColor);
        if (bc && bc.a > 0.05) {
          if (dropBg(bc) && !llevaImagen) st.backgroundColor = 'transparent';
          else fondoPropio = true;
        }
      }
      if (fondoPropio) conFondo.add(el);

      // El color del texto SOLO se retoca cuando el elemento no vive sobre un
      // fondo elegido por el autor. Si puso fondo y color juntos (un botón de
      // marca: texto blanco sobre morado), la pareja ya es legible y quitarle
      // el color deja el texto invisible —que es justo lo que pasaba—.
      if (fondoPropio || heredaFondo) return;
      if (el.tagName === 'FONT') {
        const fc = parseColor(el.getAttribute('color'));
        if (fc && dropFg(fc)) el.removeAttribute('color');
      }
      if (st && st.length) {
        const fc = parseColor(st.color);
        if (fc && dropFg(fc)) st.removeProperty('color');
      }
    });
    return doc.body.innerHTML;
  } catch {
    return html;
  }
}

function bodyDoc(html, tk, adapt) {
  const scheme = adapt ? tk.scheme : 'light';
  const htmlBg = adapt ? tk.panel2 : '#ffffff';
  const textColor = adapt ? tk.text2 : '#1f2430';
  const linkColor = adapt ? tk.accent : '#2b5bd7';
  const lineColor = adapt ? tk.line : '#d6d9e6';
  // Estructura con especificidad normal (de ella depende la medición del iframe);
  // tema con :where() → especificidad CERO, cualquier estilo del correo gana.
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><base target="_blank"><style>`
    + `html,body{margin:0;padding:0;height:auto!important;min-height:0!important;overflow:visible!important}`
    + `body{padding:4px;word-wrap:break-word;overflow-wrap:break-word}`
    + `img{max-width:100%;height:auto}table{max-width:100%}`
    + `:where(html){color-scheme:${scheme};background:${htmlBg}}`
    + `:where(body){background:transparent;color:${textColor};font-family:Inter,system-ui,-apple-system,"Segoe UI",sans-serif;line-height:1.7;font-size:15px}`
    + `:where(p,div,span,td,th,li,h1,h2,h3,h4,h5,h6,blockquote){color:inherit}`
    + `:where(a){color:${linkColor}}`
    + `:where(hr){border-color:${lineColor}}`
    // Sin `:where(table){background:transparent}`: aunque tenga especificidad cero
    // gana a los atributos `bgcolor`, y borraba los fondos que adaptEmailColors
    // decide CONSERVAR (dejaba texto oscuro sobre el lector oscuro: recibos de Stripe).
    + `:where(blockquote){border-left:3px solid ${lineColor};padding-left:12px;margin-left:0}`
    + `</style></head><body>${html}</body></html>`;
}

function emailSrcDoc(msg, adapt = true) {
  const html = msg.html || (msg.text ? `<pre style="white-space:pre-wrap;font-family:inherit">${(msg.text || '').replace(/</g, '&lt;')}</pre>` : '<p>(Sin contenido)</p>');
  const clean = DOMPurify.sanitize(html, { WHOLE_DOCUMENT: false, ADD_ATTR: ['target'] });
  const tk = themeTokens();
  return bodyDoc(adapt ? adaptEmailColors(clean, tk.dark) : clean, tk, adapt);
}

/* Preferencia persistida del conmutador "Adaptar al tema / Colores originales" */
const BODY_THEME_KEY = 'zuno_mail_theme_body';
function readAdaptPref() {
  try { return localStorage.getItem(BODY_THEME_KEY) !== 'original'; } catch { return true; }
}

function snoozePresets() {
  const now = new Date();
  const later = new Date(now.getTime() + 3 * 3600 * 1000);
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(8, 0, 0, 0);
  const weekend = new Date(now);
  const day = weekend.getDay();
  const add = day <= 6 ? (6 - day + 7) % 7 || 7 : 7;
  weekend.setDate(weekend.getDate() + (day === 6 ? 7 : add));
  weekend.setHours(9, 0, 0, 0);
  const nextWeek = new Date(now);
  nextWeek.setDate(nextWeek.getDate() + 7);
  nextWeek.setHours(8, 0, 0, 0);
  return [
    { id: 'later', label: 'Más tarde hoy', at: later },
    { id: 'tomorrow', label: 'Mañana por la mañana', at: tomorrow },
    { id: 'weekend', label: 'Este fin de semana', at: weekend },
    { id: 'week', label: 'La próxima semana', at: nextWeek },
  ];
}

const FOLDER_LABEL = {
  inbox: 'Recibidos', archive: 'Archivo', spam: 'Spam', trash: 'Papelera',
  sent: 'Enviados', drafts: 'Borradores',
};

function extOf(filename) {
  const m = /\.([a-z0-9]{1,5})$/i.exec(filename || '');
  return (m ? m[1] : 'FILE').toUpperCase().slice(0, 4);
}

/* ---- Adjuntos que se abren en docs.orvian.mx ------------------------------
   Un documento adjunto (PDF, Word, Excel, PowerPoint…) se guarda en Drive, en
   «Correos y documentos / <remitente>», y se abre en docs.orvian.mx en OTRA
   pestaña: el correo se queda donde estaba. Qué se abre allí lo dice el backend
   (`abre_en_docs`, la misma regla que `_es_documento`); si el mensaje viene de
   una caché sin la marca, vale la extensión. */
const abreEnDocs = (att) => att.abre_en_docs ?? OFFICE_FILE_RE.test(att.filename || '');

/** La app de Orvian Office que abre el adjunto (para decir «Abrir en Orvian Sheets»). */
function appDeAdjunto(nombre) {
  const n = (nombre || '').toLowerCase();
  return OFFICE_APPS[/\.(csv|tsv)$/.test(n) ? 'sheet' : officeKind(n, 'doc')] || OFFICE_APPS.doc;
}

/** «/Correos y documentos/Juan Pérez» → «Correos y documentos › Juan Pérez». */
const rutaLegible = (ruta) => (ruta || '').split('/').filter(Boolean).join(' › ') || 'Mi unidad';

/* La pestaña se abre DENTRO del clic, en blanco y con un «Abriendo…» en los
   colores del tema; la dirección del documento se le pone cuando el backend
   responde. Abierta después de la petición, el navegador la bloquearía.
   Devuelve null si el navegador no deja abrirla. */
function pestanaDeEspera(nombre) {
  let w = null;
  try { w = window.open('', '_blank'); } catch { w = null; }
  if (!w) return null;
  try {
    w.opener = null; // la pestaña del documento no necesita mandar sobre la del correo
    const t = themeTokens();
    const d = w.document;
    d.title = `Abriendo ${nombre || 'documento'}…`;
    d.body.style.cssText = `margin:0;min-height:100vh;display:grid;place-items:center;background:${t.panel2};`
      + `color:${t.text2};font:15px system-ui,-apple-system,"Segoe UI",sans-serif;color-scheme:${t.scheme}`;
    const p = d.createElement('p');
    p.textContent = `Abriendo ${nombre || 'el documento'} en ${OFFICE_NAME}…`; // texto, nunca HTML: el nombre viene del correo
    d.body.appendChild(p);
  } catch { /* sin el aviso la pestaña sirve igual */ }
  return w;
}

/* Fichas de los adjuntos de un correo: las usan el correo abierto y cada mensaje desplegado de la
   conversación (antes, en el hilo solo se veía el cuerpo y sus adjuntos se perdían). */
function AdjuntosLista({ adjuntos, ocupado, onAbrir, onDrive, onDescargar }) {
  if (!adjuntos?.length) return null;
  return (
    <div style={{ marginTop: 22 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 10 }}>
        {adjuntos.length} {adjuntos.length === 1 ? 'archivo adjunto' : 'archivos adjuntos'}
      </div>
      <div className="flex flex-wrap gap-2.5" data-testid="attachments">
        {adjuntos.map((att) => {
          const enDocs = abreEnDocs(att);
          const app = enDocs ? appDeAdjunto(att.filename) : null;
          const trabajando = ocupado === att.index;
          const textoDrive = att.drive ? `En Drive · ${rutaLegible(att.drive.carpeta)}` : 'Guardar en Drive';
          return (
            <div key={att.index} className="od-attach-card">
              <button
                type="button"
                onClick={() => onAbrir(att)}
                className="od-attach-main"
                data-testid="attachment-chip"
                title={enDocs ? `Abrir en ${app.name}` : att.filename}
                aria-busy={trabajando || undefined}
              >
                <span className="od-attach-ext">{extOf(att.filename)}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate" style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{att.filename}</span>
                  <span className="block truncate" style={{ fontSize: 12, color: 'var(--text-3)' }}>
                    {humanSize(att.size)}
                    {enDocs && <> · <span style={{ color: 'var(--accent)' }}>Abrir en {app.name}</span></>}
                  </span>
                </span>
                {trabajando
                  ? <Loader2 className="h-4 w-4 shrink-0 od-spin" style={{ color: 'var(--accent)' }} />
                  : enDocs && <ExternalLink className="h-4 w-4 shrink-0" style={{ color: 'var(--text-3)' }} />}
              </button>
              <span className="od-attach-acts">
                <Tooltip><TooltipTrigger asChild>
                  <button
                    type="button"
                    className="od-attach-act"
                    onClick={() => onDrive(att)}
                    disabled={trabajando}
                    aria-label={textoDrive}
                    data-on={att.drive ? 'true' : undefined}
                    data-testid="attachment-save-drive"
                  >
                    {att.drive ? <Check className="h-4 w-4" /> : <HardDriveUpload className="h-4 w-4" />}
                  </button>
                </TooltipTrigger><TooltipContent>{textoDrive}</TooltipContent></Tooltip>
                <Tooltip><TooltipTrigger asChild>
                  <button
                    type="button"
                    className="od-attach-act"
                    onClick={() => onDescargar(att)}
                    aria-label={`Descargar ${att.filename}`}
                    data-testid="attachment-download"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                </TooltipTrigger><TooltipContent>Descargar</TooltipContent></Tooltip>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* Mensaje contraído de la conversación, bajo el correo principal. */
function ThreadItem({ t, fallbackFolder, adaptBody, theme, acciones, ocupado }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const folder = t.folder || fallbackFolder;
  const origen = { acc: t.accountId, folder, uid: t.uid, mid: t.message_id || null };
  const { data: m, isFetching } = useQuery({
    queryKey: ['message', t.accountId, folder, t.uid],
    queryFn: async () => (await api.get(`/mail/message?account_id=${t.accountId}&folder=${folder}&uid=${t.uid}&mark_read=false&img_proxy=1`)).data,
    enabled: open,
  });
  // `theme` entra en las deps para reconstruir el documento al cambiar de tema.
  const srcDoc = useMemo(() => (m ? emailSrcDoc(m, adaptBody) : ''), [m, adaptBody, theme]);
  const [avBg, avFg] = avatarTone(t.from?.email);
  const fit = () => { try { const el = ref.current; const doc = el.contentWindow.document; el.style.height = (Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight) + 8) + 'px'; } catch {} };
  const onLoad = () => { fit(); [150, 600, 1500].forEach((d) => setTimeout(fit, d)); };
  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--line)', background: 'var(--bg)' }} data-testid="thread-item">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-[var(--hover)]"
        data-testid="thread-item-header"
      >
        <Avatar className="h-8 w-8 shrink-0">
          <AvatarFallback style={{ background: avBg, color: avFg }} className="text-[11px] font-semibold">
            {initials(t.from?.name, t.from?.email)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="text-sm truncate" style={{ color: 'var(--text)', fontWeight: t.seen ? 500 : 700 }}>{t.from?.name || t.from?.email}</div>
          <div className="text-xs truncate" style={{ color: 'var(--text-3)' }}>{t.from?.email}</div>
        </div>
        {t.has_attachments && <Paperclip className="h-3.5 w-3.5 shrink-0" style={{ color: 'var(--text-3)' }} />}
        <span className="text-xs shrink-0" style={{ color: 'var(--text-3)' }}>{fmtDate(t.date)}</span>
        {open ? <ChevronUp className="h-4 w-4 shrink-0" style={{ color: 'var(--text-3)' }} /> : <ChevronDown className="h-4 w-4 shrink-0" style={{ color: 'var(--text-3)' }} />}
      </button>
      {open && (
        isFetching && !m ? (
          <div className="py-6 grid place-items-center"><Loader2 className="h-5 w-5 od-spin" style={{ color: 'var(--accent)' }} /></div>
        ) : m ? (
          <div className="px-3 pb-3 pt-2" style={{ borderTop: '1px solid var(--line)' }}>
            <iframe ref={ref} title={`thread-${t.uid}`} sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox" srcDoc={srcDoc} className="w-full border-0 email-body block" style={{ minHeight: 60 }} scrolling="no" onLoad={onLoad} />
            <AdjuntosLista
              adjuntos={(m.attachments || []).filter((a) => !a.inline)}
              ocupado={ocupado?.uid === String(t.uid) ? ocupado.index : null}
              onAbrir={(att) => acciones.abrir(att, origen)}
              onDrive={(att) => acciones.drive(att, origen)}
              onDescargar={(att) => acciones.descargar(att, origen)}
            />
          </div>
        ) : null
      )}
    </div>
  );
}

export default function Reader({ mobile }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  // Selectores sueltos (B13): con useStore() entero, cada tecla del buscador, cada aviso o el estado del
  // WebSocket volvían a pintar el lector y a sanear el correo completo.
  const activeAccountId = useStore((s) => s.activeAccountId);
  const activeFolder = useStore((s) => s.activeFolder);
  const selectedAccountId = useStore((s) => s.selectedAccountId);
  const selectedFolder = useStore((s) => s.selectedFolder);
  const selectedUid = useStore((s) => s.selectedUid);
  const setSelectedUid = useStore((s) => s.setSelectedUid);
  const openMessage = useStore((s) => s.openMessage);
  const openComposer = useStore((s) => s.openComposer);
  const fullscreen = useStore((s) => s.fullscreen);
  const toggleFullscreen = useStore((s) => s.toggleFullscreen);
  const theme = useStore((s) => s.theme);
  const accId = selectedAccountId || activeAccountId;
  const fld = selectedFolder || activeFolder;
  const isSpam = fld === 'spam';
  const [showImages, setShowImages] = useState(!isSpam);
  const [adaptBody, setAdaptBody] = useState(readAdaptPref);
  const [sourceOpen, setSourceOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [adjuntoOcupado, setAdjuntoOcupado] = useState(null); // {uid, index} del adjunto que va a Drive / a docs
  const [borrado, setBorrado] = useState(null);
  const [customSnoozeDate, setCustomSnoozeDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10);
  });
  const [customSnoozeTime, setCustomSnoozeTime] = useState('09:00');
  const iframeRef = useRef(null);
  const wrapRef = useRef(null);

  const { data: msg, isLoading, isError: errorMsg, error: errorDeMsg, refetch: reintentarMsg } = useQuery({
    queryKey: ['message', accId, fld, selectedUid],
    queryFn: async () => (await api.get(`/mail/message?account_id=${accId}&folder=${fld}&uid=${selectedUid}&img_proxy=1`)).data,
    enabled: !!selectedUid && !!accId,
  });

  /* Carpetas propias del buzón: mover a "Clientes" tiene que estar donde ya
     está "Mover a Papelera", no escondido en otra pantalla. */
  const { data: arbolCarpetas } = useQuery({
    queryKey: ['mail-folders', accId],
    queryFn: async () => (await api.get(`/mail/folders?account_id=${accId}`)).data,
    enabled: !!accId,
    staleTime: 120000,
  });
  const carpetasPropias = useMemo(() => {
    const sistema = new Set(['inbox', 'sent', 'drafts', 'trash', 'spam', 'archive']);
    return (arbolCarpetas?.flat || [])
      .filter((f) => f.selectable !== false && !f.logical && !sistema.has((f.name || '').toLowerCase())
        && !sistema.has((f.label || '').toLowerCase()))
      .filter((f) => f.name !== fld)
      .slice(0, 40);
  }, [arbolCarpetas, fld]);

  // Ya cacheado por MailApp; aquí solo sirve para saber qué dominios son propios.
  const { data: accounts = [] } = useQuery({
    queryKey: ['accounts'],
    queryFn: async () => {
      const res = await api.get('/accounts');
      return Array.isArray(res.data) ? res.data : (res.data?.accounts || []);
    },
    staleTime: 300000,
  });

  // Conversación: mensajes hermanos desde la caché de la lista. Antes se leía ['unified', fld] con
  // useQuery, pero la lista guarda bajo ['unified', carpeta, categoría] (y las búsquedas bajo
  // ['search-unified', …]): la clave nunca coincidía y «Ver N mensajes más» no salía jamás (B4).
  // Se juntan todas las listas en caché; son infinite queries ({pages:[{messages}]}).
  const [threadOpen, setThreadOpen] = useState(false);
  const normSubject = (s) => (s || '').replace(/^((re|fwd|rv|fw):\s*)+/i, '').trim().toLowerCase();
  const thread = useMemo(() => {
    if (!msg) return [];
    const all = [];
    const vistos = new Set();
    for (const prefijo of [['unified'], ['search-unified']]) {
      for (const [, data] of qc.getQueriesData({ queryKey: prefijo })) {
        const filas = data?.pages ? data.pages.flatMap((p) => p?.messages || []) : (data?.messages || []);
        for (const m of filas) {
          const k = m.message_id ? `${m.accountId}|${m.message_id}` : `${m.accountId}|${m.folder}|${m.uid}`;
          if (!vistos.has(k)) { vistos.add(k); all.push(m); }
        }
      }
    }
    // Mismo criterio que la lista (M11): la conversación es su thread_id (References/In-Reply-To);
    // el asunto solo cuando no lo hay. Antes el lector agrupaba siempre por asunto.
    const propio = all.find((m) => m.accountId === accId && String(m.uid) === String(selectedUid));
    const hilo = msg.thread_id || propio?.thread_id;
    if (hilo) {
      return all
        .filter((m) => m.accountId === accId && m.thread_id === hilo)
        .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    }
    const key = normSubject(msg.subject);
    if (!key) return [];
    return all
      .filter((m) => m.accountId === accId && !m.thread_id && normSubject(m.subject) === key)
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msg, accId, selectedUid]);
  const otherThread = useMemo(() => thread.filter((t) => !(t.uid === selectedUid && t.accountId === accId)), [thread, selectedUid, accId]);
  useEffect(() => { setThreadOpen(false); setDetailsOpen(false); }, [selectedUid]);

  useEffect(() => { setShowImages(fld !== 'spam'); }, [selectedUid, fld]);
  useEffect(() => {
    if (!msg) return;
    // Marca leído en la caché de la lista sin refetch IMAP multi-cuenta.
    const markSeen = (messages) => {
      let changed = false;
      const next = messages.map((m) => {
        // Misma cuenta, uid Y carpeta (B5): el mismo uid existe en otras carpetas y es otro correo
        if (String(m.uid) === String(msg.uid) && m.accountId === accId && (!m.folder || m.folder === fld) && !m.seen) {
          changed = true;
          return { ...m, seen: true };
        }
        return m;
      });
      return changed ? next : null;
    };
    const update = (old) => {
      if (!old) return old;
      if (old.pages) {
        let any = false;
        const pages = old.pages.map((p) => {
          const next = p?.messages ? markSeen(p.messages) : null;
          if (next) { any = true; return { ...p, messages: next }; }
          return p;
        });
        return any ? { ...old, pages } : old;
      }
      if (old.messages) {
        const next = markSeen(old.messages);
        return next ? { ...old, messages: next } : old;
      }
      return old;
    };
    qc.setQueriesData({ queryKey: ['unified'] }, update);
    qc.setQueriesData({ queryKey: ['unified-badge'] }, update);
    // También en los resultados de búsqueda (QA 2026-10-06: el correo abierto seguía «sin leer» ahí)
    qc.setQueriesData({ queryKey: ['search-unified'] }, update);
  }, [msg?.uid]);

  const resizeIframe = () => {
    const el = iframeRef.current;
    const wrap = wrapRef.current;
    if (!el || !el.contentWindow || !wrap) return;
    try {
      const doc = el.contentWindow.document;
      // reset antes de medir
      el.style.transform = 'none';
      el.style.width = '100%';
      const containerW = wrap.clientWidth;
      const naturalW = Math.max(doc.body ? doc.body.scrollWidth : 0, doc.documentElement ? doc.documentElement.scrollWidth : 0, containerW);
      const overflow = naturalW > containerW + 2;
      if (overflow) el.style.width = naturalW + 'px';  // maqueta a su ancho natural y luego escala
      const contentH = Math.max(doc.body ? doc.body.scrollHeight : 0, doc.documentElement ? doc.documentElement.scrollHeight : 0);
      const scale = overflow ? containerW / naturalW : 1;
      if (scale < 1) { el.style.transformOrigin = 'top left'; el.style.transform = `scale(${scale})`; }
      el.style.height = contentH + 'px';
      const newH = (contentH * scale + 8) + 'px';
      if (wrap.style.height !== newH) wrap.style.height = newH;
    } catch {}
  };

  const onIframeLoad = () => {
    resizeIframe();
    [150, 500, 1200, 2500].forEach((t) => setTimeout(resizeIframe, t));
    try {
      const win = iframeRef.current.contentWindow;
      win.addEventListener('load', resizeIframe);
      win.document.querySelectorAll('img').forEach((img) => { if (!img.complete) { img.addEventListener('load', resizeIframe); img.addEventListener('error', resizeIframe); } });
    } catch {}
  };

  useEffect(() => {
    let lastW = wrapRef.current ? wrapRef.current.clientWidth : 0;
    let raf = 0;
    const onResize = () => {
      const w = wrapRef.current ? wrapRef.current.clientWidth : 0;
      if (w === lastW) return;          // ignora cambios de alto (evita bucle del ResizeObserver)
      lastW = w;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(resizeIframe);
    };
    window.addEventListener('resize', onResize);
    let ro;
    if (wrapRef.current && 'ResizeObserver' in window) {
      ro = new ResizeObserver(onResize);
      ro.observe(wrapRef.current);
    }
    return () => { window.removeEventListener('resize', onResize); cancelAnimationFrame(raf); if (ro) ro.disconnect(); };
  }, [msg?.uid]);

  const toggleAdaptBody = () => {
    setAdaptBody((v) => {
      const next = !v;
      try { localStorage.setItem(BODY_THEME_KEY, next ? 'adapt' : 'original'); } catch {}
      return next;
    });
  };

  // Las imágenes incrustadas que el cuerpo ya enseña (el backend las marca
  // `inline`) no se repiten como fichas de adjunto, como en Gmail.
  const adjuntosVisibles = (msg?.attachments || []).filter((a) => !a.inline);

  // O4 (auditoría 2026-10-09): con un Word/Excel adjunto, el editor se empieza a bajar ya, antes del clic
  const tiposAdjuntos = tiposDeAdjuntos(adjuntosVisibles.filter(abreEnDocs).map((a) => a.filename)).join(',');
  useEffect(() => {
    if (tiposAdjuntos) precalentarMotor({ tipos: tiposAdjuntos.split(',') });
  }, [tiposAdjuntos]);

  const renderBody = () => {
    if (!msg) return '';
    const html = msg.html || (msg.text ? `<pre style="white-space:pre-wrap;font-family:inherit">${msg.text.replace(/</g, '&lt;')}</pre>` : '<p>(Sin contenido)</p>');
    let clean = DOMPurify.sanitize(html, { WHOLE_DOCUMENT: false, ADD_ATTR: ['target'] });
    if (!showImages) clean = clean.replace(/<img[^>]*src=/gi, '<img data-blocked="1" data-src=');
    const tk = themeTokens();
    return bodyDoc(adaptBody ? adaptEmailColors(clean, tk.dark) : clean, tk, adaptBody);
  };

  /* Atajos del correo abierto (B10, auditoría 2026-10-05), los de Gmail: r responder, a responder a todos,
     f reenviar, e archivar, # eliminar. Nunca mientras se escribe ni con un diálogo o el redactor abierto. */
  useEffect(() => {
    if (!msg) return undefined;
    const tecla = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const el = document.activeElement;
      if (/input|textarea|select/i.test(el?.tagName) || el?.isContentEditable) return;
      if (document.querySelector('[role="dialog"][data-state="open"], [data-testid="composer"]')) return;
      const acciones = { r: () => buildReply('reply'), a: () => buildReply('replyAll'), f: () => buildReply('forward'),
        e: () => doAction('archive'), '#': () => doAction('trash') };
      const hacer = acciones[e.key];
      if (hacer) { e.preventDefault(); hacer(); }
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  });   // sin dependencias a propósito: siempre con el correo y las funciones de este render

  // Saneado y adaptado UNA vez por correo/ajuste (B13), no en cada render: cambiar srcDoc además recarga el iframe.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cuerpoHtml = useMemo(() => renderBody(), [msg, showImages, adaptBody, theme]);

  const invalidateLists = () => {
    qc.invalidateQueries({ queryKey: ['unified'] });
    qc.invalidateQueries({ queryKey: ['unified-badge'] });
  };

  const undo = async (fromFolder, toFolder, messageId) => {
    try {
      await api.post('/mail/undo-move', { account_id: accId, from_folder: fromFolder, to_folder: toFolder, message_id: messageId });
      invalidateLists();
      toast.success('Restaurado');
    } catch { toast.error('No se pudo deshacer'); }
  };

  /* Qué queda abierto tras sacar este correo de la carpeta (M32): por defecto, la lista; con la opción
     de Ajustes, el siguiente. Se calcula ANTES de mover: después, la lista ya no lo tiene. */
  const cerrarOSeguir = (siguiente) => {
    if (siguiente) openMessage(siguiente.accountId, siguiente.folder, siguiente.uid);
    else setSelectedUid(null);
  };
  const elSiguiente = () => {
    const { trasMover, ordenLista } = useStore.getState();
    return trasMover === 'siguiente' ? siguienteTrasMover(ordenLista, { accountId: accId, uid: selectedUid }) : null;
  };

  const doAction = async (type) => {
    const folderAtAction = fld;
    const mid = msg?.message_id;
    const siguiente = elSiguiente();
    try {
      if (type === 'archive') {
        await api.post('/mail/move', { account_id: accId, folder: fld, uids: [selectedUid], target: 'archive' });
        if (mid) toast.success(avisoArchivado(activeFolder, !!msg?.flagged), { duration: 6000, action: { label: 'Deshacer', onClick: () => undo('archive', folderAtAction, mid) } });
        else toast.success(avisoArchivado(activeFolder, !!msg?.flagged));
      }
      if (type === 'trash') {
        // En la Papelera es definitivo: sin "Deshacer" y con confirmación.
        if (fld === 'trash') {
          const uid = selectedUid;
          setBorrado({
            cantidad: 1,
            ejecutar: async () => {
              try {
                await api.post('/mail/delete', { account_id: accId, folder: fld, uids: [uid], target: 'trash' });
                toast.success(avisoEliminados(1));
                invalidateLists();
                cerrarOSeguir(siguiente);
              } catch (err) {
                toast.error(err?.response?.data?.detail || 'No se pudo eliminar');
              }
            },
          });
          return;
        }
        await api.post('/mail/delete', { account_id: accId, folder: fld, uids: [selectedUid], target: 'trash' });
        if (mid) toast.success('Enviado a la papelera', { duration: 6000, action: { label: 'Deshacer', onClick: () => undo('trash', folderAtAction, mid) } });
        else toast.success('Movido a papelera');
      }
      if (type === 'spam') {
        await api.post('/mail/move', { account_id: accId, folder: fld, uids: [selectedUid], target: 'spam' });
        const fromEmail = msg?.from?.email;
        if (mid) {
          // «Deshacer» siempre; «Filtrar remitente» al lado, no en su lugar (M12).
          toast.success('Marcado como spam', {
            duration: 9000,
            action: { label: 'Deshacer', onClick: () => undo('spam', folderAtAction, mid) },
            ...(fromEmail ? {
              cancel: {
                label: 'Filtrar remitente',
                onClick: async () => {
                  try {
                    await api.post('/rules', {
                      name: `Spam: ${fromEmail}`, field: 'from', op: 'contains',
                      value: fromEmail, action: 'move', action_value: 'spam', enabled: true,
                    });
                    toast.success('Filtro creado: correos de este remitente irán a Spam');
                    qc.invalidateQueries({ queryKey: ['rules'] });
                  } catch { toast.error('No se pudo crear el filtro'); }
                },
              },
            } : {}),
          });
        } else toast.success('Marcado como spam');
      }
      if (type === 'notspam') {
        await api.post('/mail/move', { account_id: accId, folder: fld, uids: [selectedUid], target: 'inbox' });
        if (mid) toast.success('Marcado como no spam · movido a Recibidos', { duration: 7000, action: { label: 'Deshacer', onClick: () => undo('inbox', folderAtAction, mid) } });
        else toast.success('No es spam');
      }
      invalidateLists();
      cerrarOSeguir(siguiente);
    } catch (err) { toast.error(mensajeErrorRed(err, 'completar la acción')); }
  };

  /* Mover un correo a una carpeta propia casi siempre significa "y los que
     vengan igual, también". Se ofrece justo ahí, en el aviso de que se movió:
     es el único momento en que la persona está pensando en eso. */
  const enviarSiempreAqui = async (carpeta, remitente, etiqueta) => {
    if (!remitente) { toast.error('Este correo no tiene remitente'); return; }
    try {
      const r = await api.post('/rules', {
        name: `${remitente} → ${etiqueta}`,
        field: 'from', op: 'contains', value: remitente,
        action: 'move', action_value: carpeta, enabled: true, apply_existing: false,
      });
      toast.success(`Listo: lo que llegue de ${remitente} irá a ${etiqueta}`, {
        description: 'Puedes cambiarlo en Configuración → Reglas.',
        duration: 7000,
      });
      qc.invalidateQueries({ queryKey: ['rules'] });
      return r;
    } catch { toast.error('No se pudo crear la regla'); }
  };

  const moveTo = async (target) => {
    const folderAtAction = fld;
    const mid = msg?.message_id;
    const propia = !FOLDER_LABEL[target];
    const etiqueta = FOLDER_LABEL[target]
      || carpetasPropias.find((f) => f.name === target)?.label
      || target;
    const remitente = (msg?.from?.email || '').toLowerCase();
    try {
      await api.post('/mail/move', { account_id: accId, folder: fld, uids: [selectedUid], target });
      if (propia && remitente) {
        toast.success(`Movido a ${etiqueta}`, {
          description: `¿Enviar aquí también los próximos de ${remitente}?`,
          duration: 9000,
          action: { label: 'Hacerlo siempre', onClick: () => enviarSiempreAqui(target, remitente, etiqueta) },
          cancel: mid ? { label: 'Deshacer', onClick: () => undo(target, folderAtAction, mid) } : undefined,
        });
      } else if (mid) {
        toast.success(`Movido a ${etiqueta}`, { duration: 6000, action: { label: 'Deshacer', onClick: () => undo(target, folderAtAction, mid) } });
      } else {
        toast.success(`Movido a ${etiqueta}`);
      }
      invalidateLists();
      setSelectedUid(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo mover');
    }
  };

  const toggleStar = async () => {
    if (!msg) return;
    const next = !msg.flagged;
    try {
      await api.post('/mail/flag', { account_id: accId, folder: fld, uids: [String(selectedUid)], flag: 'flagged', value: next });
      qc.setQueryData(['message', accId, fld, selectedUid], (old) => (old ? { ...old, flagged: next } : old));
      const patch = (messages) => messages.map((m) => (m.uid === msg.uid && m.accountId === accId ? { ...m, flagged: next } : m));
      qc.setQueriesData({ queryKey: ['unified'] }, (old) => {
        if (!old) return old;
        if (old.pages) return { ...old, pages: old.pages.map((p) => (p?.messages ? { ...p, messages: patch(p.messages) } : p)) };
        if (old.messages) return { ...old, messages: patch(old.messages) };
        return old;
      });
    } catch { toast.error('No se pudo cambiar el destacado'); }
  };


  const snoozeUntil = async (at) => {
    try {
      await api.post('/mail/snooze', {
        account_id: accId, folder: fld, uid: String(selectedUid), until: at.toISOString(),
        message_id: msg?.message_id || null, subject: msg?.subject || '',
        from_email: msg?.from?.email || '', from_name: msg?.from?.name || '',
      });
      toast.success(`Pospuesto hasta ${textoProgramado(at)}`);
      invalidateLists();
      qc.invalidateQueries({ queryKey: ['snoozed'] });
      setSelectedUid(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo posponer');
    }
  };

  /* Clasificar al remitente a mano.

     La heurística acierta casi siempre pero nunca del todo, y la persona que lee
     el correo sabe mejor que ninguna regla dónde va cada remitente. Esto NO
     mueve, archiva ni borra nada: solo cambia la pestaña en la que aparece, y
     se aplica de golpe a todo lo que ya está en la caché de ese remitente. */
  const clasificarRemitente = async (categoria, scope = 'sender') => {
    const email = msg?.from?.email;
    if (!email) { toast.error('Sin remitente'); return; }
    try {
      const r = (await api.post('/mail/categories/sender', { email, category: categoria, scope })).data;
      const donde = CATEGORIAS_NOMBRE[categoria] || categoria;
      toast.success(r.updated > 0
        ? `${r.sender} va a ${donde} · ${r.updated} correo(s) reclasificados`
        : `${r.sender} va a ${donde}`);
      qc.invalidateQueries({ queryKey: ['unified'] });
      qc.invalidateQueries({ queryKey: ['unified-badge'] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo clasificar el remitente');
    }
  };

  const dominioRemitente = ((msg?.from?.email || '').split('@')[1] || '').toLowerCase();

  const createFilterFromSender = async () => {
    const email = msg?.from?.email;
    if (!email) { toast.error('Sin remitente'); return; }
    try {
      const r = await api.post('/rules', {
        name: `Saltar inbox: ${email}`, field: 'from', op: 'contains', value: email,
        action: 'skip_inbox', action_value: 'archive', enabled: true, apply_existing: true,
      });
      const n = r.data?.applied_existing || 0;
      toast.success(n > 0 ? `Filtro creado · ${n} mensaje(s) existentes archivados` : 'Filtro creado: este remitente saltará la bandeja');
      qc.invalidateQueries({ queryKey: ['rules'] });
      qc.invalidateQueries({ queryKey: ['unified'] });
    } catch {
      toast.error('No se pudo crear el filtro');
    }
  };

  const buildReply = (mode) => {
    // Respuesta clásica: cuerpo vacío + cita del mensaje original (sin IA).
    // Todo lo que viene del correo se sanea/escapa: el redactor NO es un iframe con sandbox (SEG-1).
    // Si el lector tenía las imágenes bloqueadas (Spam), la cita tampoco las carga (R9)
    const original = msg.html ? htmlParaEditor(msg.html, { bloquearImagenes: !showImages }) : escaparHtml(msg.text || '').replace(/\n/g, '<br>');
    const quoted = `<br><br><blockquote>El ${fmtFull(msg.date)}, ${escaparHtml(msg.from?.name || msg.from?.email)} escribió:<br>${original}</blockquote>`;
    openComposer(mode, {
      account_id: accId,
      // Responder a todos con el Para completo, Reply-To y sin las direcciones propias (M3).
      ...(mode === 'forward' ? { to: [], cc: [] } : destinatariosDeRespuesta(msg, mode, [
        ...accounts.map((a) => a.email), useStore.getState().user?.email,
      ])),
      subject: (mode === 'forward' ? 'Fwd: ' : 'Re: ') + (msg.subject || '').replace(/^\s*((re|rv|fw|fwd|reenv)(\[\d+\])?\s*:\s*)+/i, ''),
      html: quoted,
      in_reply_to: msg.message_id,
      // Reenviar lleva los adjuntos visibles del original (M4); el redactor los pide al servidor.
      ...(mode === 'forward' && adjuntosVisibles.length ? {
        reenviar_adjuntos: { account_id: accId, folder: fld, uid: selectedUid, indices: adjuntosVisibles.map((a) => a.index) },
      } : {}),
    });
  };

  // `o` = de qué correo es el adjunto ({acc, folder, uid, mid}); sin él, el correo abierto. Los mensajes
  // desplegados de la conversación pasan el suyo.
  const origenDe = (o) => o || { acc: accId, folder: fld, uid: selectedUid, mid: msg?.message_id || null };

  const downloadAttachment = async (att, o) => {
    const q = origenDe(o);
    try {
      const res = await api.get(`/mail/attachment?account_id=${q.acc}&folder=${q.folder}&uid=${q.uid}&index=${att.index}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = att.filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { toast.error('No se pudo descargar'); }
  };

  const previewAttachment = async (att, o) => {
    const q = origenDe(o);
    const ct = att.content_type || '';
    const name = (att.filename || '').toLowerCase();
    if (/^image\//.test(ct) || ct === 'application/pdf') {
      try {
        const res = await api.get(`/mail/attachment?account_id=${q.acc}&folder=${q.folder}&uid=${q.uid}&index=${att.index}`, { responseType: 'blob' });
        const url = URL.createObjectURL(res.data);
        setPreview({ kind: ct === 'application/pdf' ? 'pdf' : 'image', url, filename: att.filename });
      } catch { toast.error('No se pudo abrir'); }
      return;
    }
    if (/\.(zip|docx|xlsx|pptx)$/.test(name)) {
      setPreview({ kind: 'loading', filename: att.filename });
      try {
        const res = await api.get(`/mail/attachment/preview?account_id=${q.acc}&folder=${q.folder}&uid=${q.uid}&index=${att.index}`);
        const d = res.data;
        if (d.kind === 'html' || d.kind === 'zip') { setPreview({ ...d, filename: att.filename, att, origen: o }); return; }
        if (d.kind === 'error') toast.error(d.message);
        setPreview(null); downloadAttachment(att, o);
      } catch { setPreview(null); toast.error('No se pudo generar la vista previa'); downloadAttachment(att, o); }
      return;
    }
    downloadAttachment(att, o);
  };

  /* Adjunto → Drive («Correos y documentos / <remitente>»). Idempotente en el
     backend: el mismo adjunto del mismo correo es siempre el mismo archivo, así
     que abrirlo dos veces no duplica y lo escrito en docs sigue ahí. */
  const adjuntoADrive = async (att, o) => {
    const q = origenDe(o);
    return (await api.post('/mail/attachment/drive', {
      account_id: q.acc, folder: q.folder, uid: String(q.uid), index: att.index,
      message_id: q.mid || null, // si ya está en Drive, el backend ni abre el buzón
    }, { timeout: 90000 })).data; // O6 (auditoría 2026-10-09): sin límite, «Abriendo…» podía quedarse para siempre
  };
  const errorDeAdjunto = (err, porDefecto) => err.response?.data?.detail
    || (err.code === 'ECONNABORTED' ? 'El servidor tardó demasiado en traer el adjunto. Intenta de nuevo.' : porDefecto);

  // La ficha del adjunto dice que ya está en Drive sin volver a pedir el correo al IMAP.
  const anotarEnDrive = (att, r, o) => { const q = origenDe(o); qc.setQueryData(['message', q.acc, q.folder, q.uid], (old) => (old ? {
    ...old,
    attachments: (old.attachments || []).map((a) => (
      a.index === att.index ? { ...a, drive: { id: r.archivo.id, carpeta: r.carpeta } } : a
    )),
  } : old)); };

  // Un archivo tiene UNA copia con UN id: si estaba en la papelera vuelve la misma, y se dice.
  const avisoDrive = (r) => {
    const opciones = { action: { label: 'Ver carpeta', onClick: () => nav(`/drive?carpeta=${encodeURIComponent(r.carpeta)}`) } };
    if (r.restaurado) toast.success(`Recuperado de la papelera › ${rutaLegible(r.carpeta)}`, opciones);
    else if (r.nuevo) toast.success(`Guardado en Drive › ${rutaLegible(r.carpeta)}`, opciones);
    else toast(`Ya estaba en Drive › ${rutaLegible(r.carpeta)}`, opciones);
  };

  const abrirEnDocs = async (att, o) => {
    if (adjuntoOcupado !== null) { // dos clics seguidos no abren dos pestañas (y se dice por qué no pasa nada)
      toast.message('Espera: ya se está abriendo un adjunto');
      return;
    }
    const pestana = pestanaDeEspera(att.filename);
    setAdjuntoOcupado({ uid: String(origenDe(o).uid), index: att.index });
    try {
      const r = await adjuntoADrive(att, o);
      anotarEnDrive(att, r, o);
      const destino = editorUrlFor(r.archivo);
      if (pestana && !pestana.closed) pestana.location.replace(new URL(destino, window.location.origin).href);
      else irA(nav, destino); // el navegador no dejó abrir otra pestaña: en esta
      if (r.nuevo || r.restaurado) avisoDrive(r);
    } catch (err) {
      try { pestana?.close(); } catch { /* ya estaba cerrada */ }
      toast.error(errorDeAdjunto(err, `No se pudo abrir en ${appDeAdjunto(att.filename).name}`));
    } finally {
      setAdjuntoOcupado(null);
    }
  };

  const guardarEnDrive = async (att, o) => {
    if (adjuntoOcupado !== null) return;
    setAdjuntoOcupado({ uid: String(origenDe(o).uid), index: att.index });
    try {
      const r = await adjuntoADrive(att, o);
      anotarEnDrive(att, r, o);
      avisoDrive(r);
    } catch (err) {
      toast.error(errorDeAdjunto(err, 'No se pudo guardar en Drive'));
    } finally {
      setAdjuntoOcupado(null);
    }
  };

  const abrirAdjunto = (att, o) => (abreEnDocs(att) ? abrirEnDocs(att, o) : previewAttachment(att, o));

  /* «Marcar como no leído» desde el lector (M17): se cierra el correo, porque tenerlo abierto
     lo vuelve a marcar como leído. */
  const marcarNoLeido = async () => {
    try {
      await api.post('/mail/flag', { account_id: accId, folder: fld, uids: [selectedUid], flag: 'seen', value: false });
      setSelectedUid(null);
      invalidateLists();
      toast.success('Marcado como no leído');
    } catch (err) { toast.error(mensajeErrorRed(err, 'marcarlo como no leído')); }
  };

  const downloadEml = async () => {
    try {
      const res = await api.get(`/mail/raw?account_id=${accId}&folder=${fld}&uid=${selectedUid}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = `mensaje-${selectedUid}.eml`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) { toast.error(mensajeErrorRed(err, 'descargar el mensaje')); }
  };

  if (!selectedUid) return (
    <div className="h-full gmail-reader items-center justify-center">
      <div className="flex flex-col items-center justify-center gap-3 text-center p-10 flex-1">
        <div className="grid place-items-center" style={{ width: 72, height: 72, borderRadius: 20, background: 'var(--bg)', color: 'var(--text-3)' }}>
          <MailOpen className="h-8 w-8" strokeWidth={1.75} />
        </div>
        <p style={{ color: 'var(--text-2)', fontSize: 15, fontWeight: 500 }}>Selecciona un correo para leerlo</p>
      </div>
    </div>
  );
  if (isLoading) return <div className="h-full gmail-reader items-center justify-center"><div className="flex-1 grid place-items-center"><Loader2 className="h-6 w-6 od-spin" style={{ color: 'var(--accent)' }} /></div></div>;
  // Antes devolvía null: si el correo no cargaba, el lector se quedaba en blanco sin salida (M7).
  if (!msg && !errorMsg) return null;   // aún sin cuenta o sin consulta: nada que decir todavía
  if (!msg) {
    const fallo = describirErrorRed(errorDeMsg);
    return (
      <div className="h-full gmail-reader items-center justify-center" data-testid="reader-error">
        <div role="alert" className="flex flex-col items-center justify-center gap-3 text-center p-10 flex-1">
          <AlertTriangle className="h-8 w-8" style={{ color: 'var(--danger)' }} />
          <p style={{ color: 'var(--text)', fontSize: 15, fontWeight: 600 }}>{fallo.titulo}</p>
          <p style={{ color: 'var(--text-2)', fontSize: 13, maxWidth: 340, lineHeight: 1.5 }}>{fallo.texto}</p>
          <div className="flex gap-2 mt-1">
            <button type="button" className="od-btn-secondary" onClick={() => reintentarMsg()}>Reintentar</button>
            <button type="button" className="od-btn-secondary" onClick={() => setSelectedUid(null)}>Volver</button>
          </div>
        </div>
      </div>
    );
  }

  const hasBlockedImages = (msg.html || '').match(/<img[^>]*src=/i);
  /* Dominios propios: si el remitente es un compañero, su avatar puede ser su
     foto de perfil; si es una empresa externa, el logo de su dominio. */
  const internalDomains = Array.from(new Set(
    (accounts || []).map((a) => {
      const at = (a?.email || '').lastIndexOf('@');
      return at > -1 ? a.email.slice(at + 1).toLowerCase() : null;
    }).filter(Boolean),
  ));
  const toList = (msg.to || []).map((t) => t.name || t.email).filter(Boolean);
  const ccList = (msg.cc || []).map((t) => t.email).filter(Boolean);
  const extraCount = Math.max(0, toList.length - 1) + ccList.length;
  const moveTargets = ['inbox', 'archive', 'spam', 'trash'].filter((f) => f !== fld);

  return (
    <div className="h-full flex flex-col min-w-0 gmail-reader">
      <div className="gmail-reader-toolbar">
        {mobile && (
          <button type="button" className="od-tool-btn" onClick={() => setSelectedUid(null)} data-testid="reader-back-button" aria-label="Volver">
            <ArrowLeft className="h-[18px] w-[18px]" />
          </button>
        )}
        <Tooltip><TooltipTrigger asChild>
          <button type="button" className="od-tool-btn" onClick={() => doAction('archive')} data-testid="reader-archive-button" aria-label="Archivar"><Archive className="h-[18px] w-[18px]" /></button>
        </TooltipTrigger><TooltipContent>Archivar</TooltipContent></Tooltip>
        {/* En el teléfono, spam / no spam, reenviar y colores van al menú «⋮» (M22): diez iconos
            no cabían en 360 px con un tamaño que se pueda tocar (M21). */}
        {mobile ? null : isSpam ? (
          <Tooltip><TooltipTrigger asChild>
            <button type="button" className="od-tool-btn" onClick={() => doAction('notspam')} data-testid="reader-notspam-button" aria-label="No es spam">
              <MailOpen className="h-[18px] w-[18px]" />
            </button>
          </TooltipTrigger><TooltipContent>No es spam</TooltipContent></Tooltip>
        ) : (
          <Tooltip><TooltipTrigger asChild>
            <button type="button" className="od-tool-btn" onClick={() => doAction('spam')} data-testid="reader-spam-button" aria-label="Marcar como spam">
              <AlertOctagon className="h-[18px] w-[18px]" />
            </button>
          </TooltipTrigger><TooltipContent>Marcar como spam</TooltipContent></Tooltip>
        )}
        <Tooltip><TooltipTrigger asChild>
          <button type="button" className="od-tool-btn" onClick={() => doAction('trash')} data-testid="reader-trash-button" aria-label={fld === 'trash' ? 'Eliminar para siempre' : 'Eliminar'}><Trash2 className="h-[18px] w-[18px]" /></button>
        </TooltipTrigger><TooltipContent>{fld === 'trash' ? 'Eliminar para siempre' : 'Eliminar'}</TooltipContent></Tooltip>
        {!isSpam && (
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" className="od-tool-btn" data-testid="reader-snooze-button" aria-label="Posponer">
                <Clock className="h-[18px] w-[18px]" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-2 rounded-xl z-[100]" align="start">
              <div className="text-xs font-semibold px-2 py-1.5" style={{ color: 'var(--text-3)' }}>Posponer hasta…</div>
              {snoozePresets().map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors hover:bg-[var(--hover)]"
                  onClick={() => snoozeUntil(p.at)}
                  data-testid={`snooze-${p.id}`}
                >
                  <div className="font-medium" style={{ color: 'var(--text)' }}>{p.label}</div>
                  <div className="text-[11px]" style={{ color: 'var(--text-3)' }}>{textoProgramado(p.at)}</div>
                </button>
              ))}
              <div className="mt-1 pt-2 px-2 pb-1 space-y-2" style={{ borderTop: '1px solid var(--line)' }}>
                <div className="text-xs font-semibold" style={{ color: 'var(--text-3)' }}>Fecha y hora a medida</div>
                <input
                  type="date"
                  className="w-full h-10 px-2 rounded-lg text-sm"
                  style={{ border: '1px solid var(--line)', background: 'var(--bg)', color: 'var(--text)' }}
                  value={customSnoozeDate}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setCustomSnoozeDate(e.target.value)}
                  data-testid="snooze-custom-date"
                />
                <input
                  type="time"
                  className="w-full h-10 px-2 rounded-lg text-sm"
                  style={{ border: '1px solid var(--line)', background: 'var(--bg)', color: 'var(--text)' }}
                  value={customSnoozeTime}
                  onChange={(e) => setCustomSnoozeTime(e.target.value)}
                  data-testid="snooze-custom-time"
                />
                <button
                  type="button"
                  className="od-btn-primary justify-center w-full"
                  data-testid="snooze-custom-apply"
                  onClick={() => {
                    if (!customSnoozeDate) { toast.error('Elige una fecha'); return; }
                    const [hh, mm] = (customSnoozeTime || '09:00').split(':').map(Number);
                    const at = new Date(`${customSnoozeDate}T00:00:00`);
                    at.setHours(hh || 9, mm || 0, 0, 0);
                    if (at <= new Date()) { toast.error('La fecha debe ser futura'); return; }
                    snoozeUntil(at);
                  }}
                >
                  Posponer a esa hora
                </button>
              </div>
            </PopoverContent>
          </Popover>
        )}
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className="od-tool-btn" data-testid="reader-move-button" aria-label="Mover">
              <FolderInput className="h-[18px] w-[18px]" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-1.5 rounded-xl z-[100] max-h-[60vh] overflow-y-auto" align="start">
            <div className="text-xs font-semibold px-2 py-1.5" style={{ color: 'var(--text-3)' }}>Mover a…</div>
            {moveTargets.map((f) => (
              <button key={f} type="button" className="od-menu-row" onClick={() => moveTo(f)} data-testid={`reader-move-${f}`}>
                <FolderInput className="h-4 w-4" style={{ color: 'var(--text-3)' }} />
                {FOLDER_LABEL[f] || f}
              </button>
            ))}
            {carpetasPropias.length > 0 && (
              <>
                <div className="my-1" style={{ height: 1, background: 'var(--line)' }} />
                <div className="text-xs font-semibold px-2 py-1.5" style={{ color: 'var(--text-3)' }}>Tus carpetas</div>
                {carpetasPropias.map((f) => (
                  <button
                    key={f.name}
                    type="button"
                    className="od-menu-row"
                    onClick={() => moveTo(f.name)}
                    data-testid={`reader-move-propia-${f.name}`}
                    style={{ paddingLeft: 8 + Math.min(f.depth || 0, 3) * 10 }}
                  >
                    <Folder className="h-4 w-4 shrink-0" style={{ color: 'var(--text-3)' }} />
                    <span className="truncate">{f.label}</span>
                  </button>
                ))}
              </>
            )}
          </PopoverContent>
        </Popover>
        <div className="flex-1" />
        {/* Responder sin bajar hasta el final: en un hilo largo (40 correos citados)
            los botones de abajo quedaban a varias pantallas. Los de abajo se quedan. */}
        {msg && (
          <>
            <Tooltip><TooltipTrigger asChild>
              <button type="button" className="od-tool-btn" onClick={() => buildReply('reply')} data-testid="reader-top-reply-button" aria-label="Responder" style={{ color: 'var(--accent)' }}>
                <Reply className="h-[18px] w-[18px]" />
              </button>
            </TooltipTrigger><TooltipContent>Responder</TooltipContent></Tooltip>
            {/* En el teléfono no cabe: va al menú «⋮» */}
            {!mobile && (
              <Tooltip><TooltipTrigger asChild>
                <button type="button" className="od-tool-btn" onClick={() => buildReply('replyAll')} data-testid="reader-top-replyall-button" aria-label="Responder a todos">
                  <ReplyAll className="h-[18px] w-[18px]" />
                </button>
              </TooltipTrigger><TooltipContent>Responder a todos</TooltipContent></Tooltip>
            )}
            {!mobile && (
              <Tooltip><TooltipTrigger asChild>
                <button type="button" className="od-tool-btn" onClick={() => buildReply('forward')} data-testid="reader-top-forward-button" aria-label="Reenviar">
                  <Forward className="h-[18px] w-[18px]" />
                </button>
              </TooltipTrigger><TooltipContent>Reenviar</TooltipContent></Tooltip>
            )}
            {!mobile && <span aria-hidden="true" style={{ width: 1, height: 20, background: 'var(--line)', margin: '0 4px', flexShrink: 0 }} />}
          </>
        )}
        {!mobile && (
          <button type="button" className="od-tool-btn" onClick={toggleFullscreen} data-testid="reader-fullscreen-button" aria-label="Pantalla completa">
            {fullscreen ? <Minimize2 className="h-[18px] w-[18px]" /> : <Maximize2 className="h-[18px] w-[18px]" />}
          </button>
        )}
        {!mobile && <Tooltip><TooltipTrigger asChild>
          <button
            type="button"
            className="od-tool-btn"
            onClick={toggleAdaptBody}
            data-testid="reader-adapt-body-button"
            aria-label={adaptBody ? 'Ver con los colores originales' : 'Adaptar al tema'}
            style={adaptBody ? undefined : { color: 'var(--accent)', background: 'var(--accent-soft)' }}
          >
            <Palette className="h-[18px] w-[18px]" />
          </button>
        </TooltipTrigger><TooltipContent>{adaptBody ? 'Viendo adaptado al tema · pulsa para ver los colores originales' : 'Viendo colores originales · pulsa para adaptar al tema'}</TooltipContent></Tooltip>}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="od-tool-btn" data-testid="reader-more-button" aria-label="Más opciones"><MoreVertical className="h-[18px] w-[18px]" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-xl">
            {mobile && msg && (
              <>
                <DropdownMenuItem onClick={() => buildReply('replyAll')} data-testid="reader-menu-replyall"><ReplyAll className="h-4 w-4 mr-2" />Responder a todos</DropdownMenuItem>
                <DropdownMenuItem onClick={() => buildReply('forward')} data-testid="reader-menu-forward"><Forward className="h-4 w-4 mr-2" />Reenviar</DropdownMenuItem>
                {isSpam ? (
                  <DropdownMenuItem onClick={() => doAction('notspam')} data-testid="reader-menu-notspam"><MailOpen className="h-4 w-4 mr-2" />No es spam</DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => doAction('spam')} data-testid="reader-menu-spam"><AlertOctagon className="h-4 w-4 mr-2" />Marcar como spam</DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={toggleAdaptBody} data-testid="reader-menu-adapt"><Palette className="h-4 w-4 mr-2" />{adaptBody ? 'Ver con los colores originales' : 'Adaptar al tema'}</DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="reader-classify-sender">
                <Inbox className="h-4 w-4 mr-2" />Clasificar remitente
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="rounded-xl">
                {Object.entries(CATEGORIAS_NOMBRE).map(([id, nombre]) => (
                  <DropdownMenuItem key={id} onClick={() => clasificarRemitente(id)} data-testid={`reader-classify-${id}`}>
                    Siempre en {nombre}
                  </DropdownMenuItem>
                ))}
                <div style={{ height: 1, background: 'var(--line)', margin: '4px 0' }} />
                {dominioRemitente && Object.entries(CATEGORIAS_NOMBRE).map(([id, nombre]) => (
                  <DropdownMenuItem key={`d-${id}`} onClick={() => clasificarRemitente(id, 'domain')}>
                    Todo @{dominioRemitente} en {nombre}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuItem onClick={createFilterFromSender} data-testid="reader-create-filter">
              <Filter className="h-4 w-4 mr-2" />Crear filtro del remitente
            </DropdownMenuItem>
            <DropdownMenuItem onClick={marcarNoLeido} data-testid="reader-mark-unread"><Mail className="h-4 w-4 mr-2" />Marcar como no leído</DropdownMenuItem>
            <DropdownMenuItem onClick={() => window.print()}><Printer className="h-4 w-4 mr-2" />Imprimir</DropdownMenuItem>
            <DropdownMenuItem onClick={downloadEml}><Download className="h-4 w-4 mr-2" />Descargar mensaje (.eml)</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setSourceOpen(true)} data-testid="reader-source-button"><Code className="h-4 w-4 mr-2" />Ver encabezados</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex-1 min-h-0 flex">
        <div className="flex-1 min-w-0 od-scroll" style={{ padding: mobile ? '18px 16px' : '26px 34px' }}>
          {/* Sin tope de ancho: el mensaje ocupa todo el panel. El mockup usaba
              760px porque su lector era estrecho; en pantallas reales dejaba un
              hueco enorme a la derecha. */}
          <div style={{ width: '100%' }}>
            {isSpam && (
              <div
                className="mb-5 px-3.5 py-2.5 rounded-xl text-sm flex items-center gap-2.5"
                style={{ background: 'var(--danger-soft)', color: 'var(--danger)', border: '1px solid var(--line)' }}
                data-testid="spam-banner"
              >
                <AlertOctagon className="h-4 w-4 shrink-0" />
                <span className="flex-1" style={{ color: 'var(--text-2)' }}>Este mensaje está en Spam. Ten cuidado con enlaces y adjuntos.</span>
                <button type="button" className="od-btn-secondary shrink-0" style={{ padding: '6px 12px' }} onClick={() => doAction('notspam')} data-testid="spam-banner-notspam">
                  No es spam
                </button>
              </div>
            )}

            <div className="flex items-start gap-3 mb-4">
              <h1 className="gmail-reader-subject break-words flex-1">{msg.subject || '(sin asunto)'}</h1>
              <button
                type="button"
                onClick={toggleStar}
                className="p-1 shrink-0"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: msg.flagged ? 'var(--accent)' : 'var(--text-3)' }}
                data-testid="reader-star-button"
                aria-label={msg.flagged ? 'Quitar destacado' : 'Destacar'}
              >
                <Star className="h-5 w-5" fill={msg.flagged ? 'currentColor' : 'none'} />
              </button>
            </div>

            {msg.priority && /1|high/i.test(msg.priority) && (
              <div className="flex flex-wrap gap-1.5 mb-5">
                <Badge variant="destructive" className="text-xs">Importante</Badge>
              </div>
            )}

            <div className="flex items-start gap-3.5 mb-2">
              <SenderAvatar
                email={msg.from?.email}
                name={msg.from?.name}
                size={44}
                internalDomains={internalDomains}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{msg.from?.name || msg.from?.email}</span>
                  {msg.from?.name && <span style={{ fontSize: 13, color: 'var(--text-3)' }}>&lt;{msg.from?.email}&gt;</span>}
                  <span className="ml-auto shrink-0" style={{ fontSize: 13, color: 'var(--text-3)' }}>{fmtFull(msg.date)}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setDetailsOpen((v) => !v)}
                  className="flex items-center gap-1.5 py-0.5 mt-0.5"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-3)' }}
                  data-testid="reader-details-toggle"
                >
                  para {toList[0] || 'mí'}{extraCount > 0 ? `, +${extraCount}` : ''}
                  <ChevronDown className="h-3.5 w-3.5 transition-transform" style={{ transform: detailsOpen ? 'rotate(180deg)' : 'none' }} />
                </button>
                {detailsOpen && (
                  <div className="od-details" data-testid="reader-details">
                    <div><span className="od-details-key">De:</span>{msg.from?.name ? `${msg.from.name} ` : ''}&lt;{msg.from?.email}&gt;</div>
                    <div><span className="od-details-key">Para:</span>{toList.join(', ') || 'mí'}</div>
                    {ccList.length > 0 && <div><span className="od-details-key">Cc:</span>{ccList.join(', ')}</div>}
                    <div><span className="od-details-key">Fecha:</span>{fmtFull(msg.date)}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Correo mío: si lo abrieron, quién y cuándo. */}
            {msg.visto && <LineaVisto visto={msg.visto} />}

            {/* Correo con invitación de calendario (iCalendar): tarjeta con la respuesta. El cuerpo sigue aislado abajo. */}
            {msg.invitacion && <TarjetaInvitacion accountId={accId} folder={fld} uid={selectedUid} />}

            {!showImages && hasBlockedImages && (
              <div className="flex items-center gap-2.5 my-4 p-2.5 rounded-xl text-sm" style={{ background: 'var(--bg)', border: '1px solid var(--line)' }}>
                <Image className="h-4 w-4 shrink-0" style={{ color: 'var(--text-3)' }} />
                <span className="flex-1" style={{ color: 'var(--text-2)' }}>{isSpam ? 'Correo en Spam: imágenes bloqueadas por seguridad.' : 'Se bloquearon imágenes remotas.'}</span>
                <button type="button" className="od-btn-secondary shrink-0" style={{ padding: '6px 12px' }} onClick={() => setShowImages(true)} data-testid="load-images-button">
                  Mostrar imágenes
                </button>
              </div>
            )}

            <div ref={wrapRef} className="w-full overflow-hidden" style={{ margin: '16px 0 4px' }}>
              <iframe ref={iframeRef} title="email-body" sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox" srcDoc={cuerpoHtml} className="border-0 email-body block" style={{ width: '100%', minHeight: 200, height: 'auto' }} scrolling="no" onLoad={onIframeLoad} />
            </div>

            <AdjuntosLista
              adjuntos={adjuntosVisibles}
              ocupado={adjuntoOcupado?.uid === String(selectedUid) ? adjuntoOcupado.index : null}
              onAbrir={(att) => abrirAdjunto(att)}
              onDrive={(att) => guardarEnDrive(att)}
              onDescargar={(att) => downloadAttachment(att)}
            />

            <div className="flex flex-wrap gap-2.5" style={{ marginTop: 28, paddingTop: 20, borderTop: '1px solid var(--line)' }}>
              <button type="button" className="od-btn-primary" onClick={() => buildReply('reply')} data-testid="reader-reply-button">
                <Reply className="h-4 w-4" />Responder
              </button>
              <button type="button" className="od-btn-secondary" onClick={() => buildReply('replyAll')} data-testid="reader-replyall-button">
                <ReplyAll className="h-4 w-4" />Responder a todos
              </button>
              <button type="button" className="od-btn-secondary" onClick={() => buildReply('forward')} data-testid="reader-forward-button">
                <Forward className="h-4 w-4" />Reenviar
              </button>
            </div>

            {otherThread.length > 0 && (
              <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--line)' }} data-testid="thread-section">
                {!threadOpen ? (
                  <button
                    onClick={() => setThreadOpen(true)}
                    data-testid="thread-show-more"
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm transition-colors hover:bg-[var(--hover)]"
                    style={{ border: '1px solid var(--line)', color: 'var(--text-2)' }}
                  >
                    <ChevronDown className="h-4 w-4" />Ver {otherThread.length} mensaje{otherThread.length > 1 ? 's' : ''} más de esta conversación
                  </button>
                ) : (
                  <div className="space-y-2" data-testid="thread-list">
                    <div className="flex items-center justify-between px-1">
                      <span className="text-sm font-semibold" style={{ color: 'var(--text-2)' }}>Conversación · {thread.length} mensajes</span>
                      <button onClick={() => setThreadOpen(false)} className="text-xs flex items-center gap-1" style={{ color: 'var(--accent)' }} data-testid="thread-hide">
                        <ChevronUp className="h-3.5 w-3.5" />Ocultar
                      </button>
                    </div>
                    {otherThread.map((t) => <ThreadItem key={`${t.accountId}-${t.uid}`} t={t} fallbackFolder={fld} adaptBody={adaptBody} theme={theme} ocupado={adjuntoOcupado} acciones={{ abrir: abrirAdjunto, drive: guardarEnDrive, descargar: downloadAttachment }} />)}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={!!preview} onOpenChange={(v) => { if (!v) { if (preview?.url) URL.revokeObjectURL(preview.url); setPreview(null); } }}>
        <DialogContent className="max-w-4xl max-h-[88vh] od-scroll">
          <DialogHeader><DialogTitle className="truncate pr-8">{preview?.filename}</DialogTitle></DialogHeader>
          {preview?.kind === 'loading' && <div className="py-12 grid place-items-center"><Loader2 className="h-6 w-6 od-spin" style={{ color: 'var(--accent)' }} /></div>}
          {preview?.kind === 'pdf' && <iframe title="pdf-preview" src={preview.url} className="w-full h-[70vh] border-0 rounded-lg" />}
          {preview?.kind === 'image' && <img src={preview.url} alt={preview.filename} className="max-w-full mx-auto rounded-lg" />}
          {preview?.kind === 'zip' && (
            <div className="max-h-[65vh] od-scroll rounded-xl" style={{ border: '1px solid var(--line)' }} data-testid="zip-preview">
              <div className="px-3 py-2 text-xs font-semibold" style={{ color: 'var(--text-3)', background: 'var(--bg)' }}>{preview.count} archivo(s) en el ZIP</div>
              {(preview.entries || []).map((e, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-1.5 text-sm" style={{ borderTop: '1px solid var(--line)' }}>
                  <Paperclip className="h-3.5 w-3.5 shrink-0" style={{ color: 'var(--text-3)' }} />
                  <span className="flex-1 truncate" style={{ color: 'var(--text)' }}>{e.name}</span>
                  <span className="text-xs shrink-0" style={{ color: 'var(--text-3)' }}>{humanSize(e.size)}</span>
                </div>
              ))}
            </div>
          )}
          {preview?.kind === 'html' && (
            <div
              className="p-4 rounded-xl max-h-[70vh] od-scroll"
              style={{ background: 'var(--panel-2)', color: 'var(--text)', border: '1px solid var(--line)' }}
              data-testid="office-preview"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.html || '') }}
            />
          )}
          {preview && (
            <Button variant="outline" className="gap-2 w-fit" onClick={() => {
              if (preview.url) { const a = document.createElement('a'); a.href = preview.url; a.download = preview.filename; a.click(); }
              else if (preview.att) downloadAttachment(preview.att, preview.origen);
            }}><Download className="h-4 w-4" />Descargar</Button>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={sourceOpen} onOpenChange={setSourceOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] od-scroll">
          <DialogHeader><DialogTitle>Encabezados del mensaje</DialogTitle></DialogHeader>
          <pre className="text-xs font-mono p-4 rounded-xl od-scroll whitespace-pre-wrap" style={{ background: 'var(--bg)', color: 'var(--text-2)', border: '1px solid var(--line)' }}>
            {Object.entries(msg.headers || {}).map(([k, v]) => `${k}: ${v}`).join('\n')}
          </pre>
        </DialogContent>
      </Dialog>

      <ConfirmarBorradoDefinitivo pendiente={borrado} onCerrar={() => setBorrado(null)} />
    </div>
  );
}
